import {
	AttachmentGallery,
	AttachmentGalleryQueryOptions,
	FilterAttachmentsResult,
} from '@nimbly-technologies/nimbly-common';
import {
	apiURLAlt,
	GALLERY_API_URL,
	GALLERY_V2_API_URL,
	GALLERY_DOWNLOAD_URL,
	GALLERY_DOWNLOAD_URL_V2,
} from 'config/baseURL';
import API, { gatewayFetch } from 'helpers/api';
import { getSessionAuthorizationFor } from 'helpers/sessionAuth';
import { SearchAttachmentResult } from 'reducers/gallery/type.d';
import { store } from 'store/configStore';

export const getData = async (url: string): Promise<any> => {
	const token = await API.getFirebaseToken();
	const response = await API.get(url, token);
	const json = await response.json();

	if (response.status !== 200) {
		throw new Error(json.message);
	}
	return json.data;
};

export const getPostData = async <T>(url: string, payload: T): Promise<any> => {
	const token = await API.getFirebaseToken();
	const response = await API.post(url, token, payload);
	const json = await response.json();

	if (response.status !== 200) {
		throw new Error(json.message);
	}
	return json.data;
};

const getGalleryApiConfig = (): { baseUrl: string } => {
	const state = store?.getState();
	const useV2 = state?.featureAccess?.features?.ATTACHMENT_GALLERY_V2 ?? false;
	const baseUrl = useV2 ? GALLERY_V2_API_URL : GALLERY_API_URL;
	return { baseUrl };
};

export const fetchGalleryAttachments = async (
	payload: Partial<AttachmentGalleryQueryOptions>,
): Promise<FilterAttachmentsResult> => {
	const { baseUrl } = getGalleryApiConfig();
	const url = `${baseUrl}/filter`;
	return await getPostData(url, payload);
};

export const fetchGalleryAttachmentById = async (id: string): Promise<AttachmentGallery> => {
	const { baseUrl } = getGalleryApiConfig();
	const url = `${baseUrl}/${id}`;
	return await getData(url);
};

/**
 * Copies this gallery image into the issue-attachment area and returns the referenceKey that the
 * create-issue endpoint accepts as an attachment.
 *
 * The copy happens server-side, addressed by the gallery row's id - no image bytes pass through
 * the browser and no path is sent, so the caller cannot name an image another organization owns.
 *
 * v2 only: the endpoint is backed by the Postgres read path, which is also the only place the
 * report linkage this feature needs is available.
 */
export const createGalleryIssueAttachment = async (
	id: string,
): Promise<{ attachment: { referenceKey: string; fileName: string; size: number } }> => {
	const { baseUrl } = getGalleryApiConfig();
	const url = `${baseUrl}/${id}/issue-attachment`;
	return await getPostData(url, {});
};

export const searchGalleryAttachment = async (
	payload: Partial<AttachmentGalleryQueryOptions>,
): Promise<SearchAttachmentResult> => {
	const { baseUrl } = getGalleryApiConfig();
	const url = `${baseUrl}/search`;
	return await getPostData(url, payload);
};

/** Authorization for a request to `target`: the session when the gateway serves it, else an internal JWT. */
export const getJwtToken = async (target: string): Promise<string> => {
	const token = await API.getFirebaseToken();
	const session = await getSessionAuthorizationFor(target, token, gatewayFetch);
	if (session) return session;
	const url = `${apiURLAlt}/auth/retrieve-jwt`;
	const response = await gatewayFetch(url, {
		headers: {
			'x-firebase-token': token,
		},
	});
	const json = await response.json();

	if (response.status !== 200) {
		throw new Error(json.message);
	}
	return json.data;
};

export interface GalleryDownloadBody {
	ids?: string | string[];
	days?: string | string[];
	months?: string | string[];
	years?: string | string[];
	sites?: string | string[];
}

export const downloadGalleryAttachment = async (body: GalleryDownloadBody, token: string): Promise<Blob> => {
	const state = store?.getState();
	const useV2 = state?.featureAccess?.features?.ATTACHMENT_GALLERY_V2 ?? false;
	const baseUrl = useV2 ? GALLERY_DOWNLOAD_URL_V2 : GALLERY_DOWNLOAD_URL;
	const response = await fetch(baseUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: token,
		},
		body: JSON.stringify(body),
	});

	if (response.status !== 200) {
		const json = await response.json();
		throw new Error(json.message);
	}

	const responseBlob = await response.blob();
	return responseBlob;
};
